import { useState } from 'react'
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line,
} from 'recharts'
import {
  ShoppingCart, Users, Package,
  ArrowRight, Clock, CheckCircle, Truck, XCircle,
  Plus, FileText, Tag, AlertTriangle, Zap, RefreshCw,
  BarChart2, Archive,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { StatCard, StatusBadge, Spinner } from '../components/ui'
import { dashboardService, reportService } from '../services/api'

const COLORS = ['#0e947a', '#FFD700', '#3B82F6', '#10B981', '#8B5CF6']

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-surface-card border border-surface-border rounded-lg p-3 shadow-xl">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
          {p.value}
        </p>
      ))}
    </div>
  )
}

const STATUS_ICONS = {
  pending:    <Clock size={13} className="text-amber-400"/>,
  processing: <Package size={13} className="text-blue-400"/>,
  shipped:    <Truck size={13} className="text-indigo-400"/>,
  delivered:  <CheckCircle size={13} className="text-green-400"/>,
  cancelled:  <XCircle size={13} className="text-red-400"/>,
}

function QuickActions() {
  const navigate = useNavigate()
  const actions = [
    { icon: Plus,      label: 'Add Product',   color: 'text-green-400',  bg: 'bg-green-400/10',  border: 'border-green-400/20', to: '/products' },
    { icon: ShoppingCart, label: 'View Orders', color: 'text-blue-400',  bg: 'bg-blue-400/10',   border: 'border-blue-400/20',  to: '/orders' },
    { icon: Users,     label: 'Customers',     color: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/20',to: '/customers' },
    { icon: FileText,  label: 'Reports',       color: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/20', to: '/reports' },
    { icon: Tag,       label: 'Categories',    color: 'text-pink-400',   bg: 'bg-pink-400/10',   border: 'border-pink-400/20',  to: '/products' },
    { icon: Zap,       label: 'Settings',      color: 'text-cyan-400',   bg: 'bg-cyan-400/10',   border: 'border-cyan-400/20',  to: '/settings' },
  ]
  return (
    <div className="bg-surface-card border border-surface-border rounded-xl p-5">
      <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
        <Zap size={16} className="text-gold"/>
        Quick Actions
      </h2>
      <div className="grid grid-cols-3 gap-3">
        {actions.map(({ icon: Icon, label, color, bg, border, to }) => (
          <button
            key={label}
            onClick={() => navigate(to)}
            className={`flex flex-col items-center gap-2 ${bg} border ${border} rounded-xl py-4 px-2 hover:scale-105 transition-transform duration-150 group`}
          >
            <div className={`w-9 h-9 rounded-lg ${bg} border ${border} flex items-center justify-center`}>
              <Icon size={18} className={color}/>
            </div>
            <span className={`text-xs font-medium ${color} text-center leading-tight`}>{label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function LowStockAlert({ items, threshold }) {
  const navigate = useNavigate()
  // items comes directly from the dashboard API: [{description, stock_code, brand, level, reorder_level, threshold}]
  if (!items?.length) return null

  return (
    <div className="bg-amber-950/30 border border-amber-700/40 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-amber-300 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-400"/>
          Low Stock Alert
          <span className="bg-amber-700/40 text-amber-300 text-xs px-2 py-0.5 rounded-full">{items.length}</span>
          {threshold != null && (
            <span className="text-gray-500 text-xs font-normal">≤ {threshold} units trigger</span>
          )}
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className="text-gray-500 text-xs flex items-center gap-1 hover:text-amber-400 transition-colors"
          >
            Set threshold
          </button>
          <button
            onClick={() => navigate('/products')}
            className="text-amber-400 text-xs flex items-center gap-1 hover:gap-2 transition-all"
          >
            Manage <ArrowRight size={12}/>
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {items.slice(0, 8).map((item, idx) => {
          const qty = item.level ?? 0
          const pct = threshold ? Math.min(100, Math.round((qty / threshold) * 100)) : 0
          const isCritical = qty === 0
          const isVeryLow = !isCritical && threshold && qty <= Math.ceil(threshold * 0.3)
          return (
            <div key={item.stock_code || idx} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <div className="min-w-0 flex items-center gap-2">
                    <p className="text-sm text-white font-medium truncate">
                      {item.description || item.stock_code || 'Unknown'}
                    </p>
                    {item.brand && (
                      <span className="text-xs text-gray-500 flex-shrink-0 hidden sm:inline">{item.brand}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {item.stock_code && (
                      <span className="text-xs text-gray-600 font-mono hidden md:inline">{item.stock_code}</span>
                    )}
                    <span className={`text-xs font-semibold ${isCritical ? 'text-red-400' : isVeryLow ? 'text-orange-400' : 'text-amber-400'}`}>
                      {isCritical ? '⚠ Out of stock' : `${qty} left`}
                    </span>
                  </div>
                </div>
                <div className="h-1.5 bg-surface-elevated rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${isCritical ? 'bg-red-500' : isVeryLow ? 'bg-orange-500' : 'bg-amber-500'}`}
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                </div>
              </div>
            </div>
          )
        })}
        {items.length > 8 && (
          <p className="text-xs text-amber-500 text-center pt-1">+{items.length - 8} more items low on stock</p>
        )}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { data: dashboard, isLoading: dashLoading, refetch: refetchDash } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: dashboardService.getStats,
    staleTime: 60 * 1000,
  })

  const { data: topProducts } = useQuery({
    queryKey: ['admin', 'top-products'],
    queryFn: () => reportService.getTopProducts(5),
    staleTime: 5 * 60 * 1000,
  })

  const { data: catBreakdown } = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: reportService.getCategoryBreakdown,
    staleTime: 5 * 60 * 1000,
  })

  const stats = dashboard || {}
  const recentOrders = stats.recent_orders || []
  const statsTopProds = stats.top_products || []
  const topProdsData = statsTopProds.length > 0 ? statsTopProds : (Array.isArray(topProducts) ? topProducts : [])
  const cats = Array.isArray(catBreakdown) ? catBreakdown.filter(c => c.product_count > 0).slice(0, 5) : []
  // Low stock comes directly from the dashboard endpoint with enriched product info + threshold
  const lowStockItems = Array.isArray(stats.low_stock) ? stats.low_stock : []
  const lowStockThreshold = lowStockItems[0]?.threshold ?? stats.low_stock_threshold ?? 10

  if (dashLoading) return (
    <div className="flex items-center justify-center h-full py-20">
      <Spinner size="lg"/>
    </div>
  )

  // Order status breakdown for chart (no money values)
  const orderStatusData = [
    { name: 'Pending',    value: stats.pending_orders   || 0, color: '#F59E0B' },
    { name: 'Processing', value: stats.processing_orders|| 0, color: '#3B82F6' },
    { name: 'Shipped',    value: stats.shipped_orders   || 0, color: '#8B5CF6' },
    { name: 'Delivered',  value: stats.delivered_orders || 0, color: '#10B981' },
    { name: 'Cancelled',  value: stats.cancelled_orders || 0, color: '#EF4444' },
  ].filter(d => d.value > 0)

  const kpis = [
    {
      title: 'Total Orders',
      value: (stats.total_orders || 0).toLocaleString(),
      sub: `${stats.today_orders || 0} new today`,
      icon: ShoppingCart,
      color: 'blue',
    },
    {
      title: 'Pending Orders',
      value: (stats.pending_orders || 0).toLocaleString(),
      sub: `${stats.processing_orders || 0} processing`,
      icon: Clock,
      color: 'amber',
    },
    {
      title: 'Customers',
      value: (stats.total_customers || 0).toLocaleString(),
      sub: 'Registered accounts',
      icon: Users,
      color: 'purple',
    },
    {
      title: 'Products',
      value: (stats.total_products || 0).toLocaleString(),
      sub: lowStockItems.length > 0 ? `⚠ ${lowStockItems.length} low stock (≤${lowStockThreshold})` : 'All stock levels OK',
      icon: Package,
      color: 'green',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">Tony's Liquor Distributor — Overview</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetchDash()}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-surface-border text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
          >
            <RefreshCw size={14}/>
          </button>
          <span className="text-xs text-gray-500 bg-surface-elevated px-3 py-1.5 rounded-lg border border-surface-border flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"/>
            Live data
          </span>
        </div>
      </div>

      {/* KPI cards — orders / customers / products only, no revenue */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map(k => <StatCard key={k.title} {...k}/>)}
      </div>

      {/* Low stock alert */}
      <LowStockAlert items={lowStockItems} threshold={lowStockThreshold}/>

      {/* Quick actions */}
      <QuickActions/>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order status breakdown chart */}
        <div className="bg-surface-card border border-surface-border rounded-xl p-5">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart2 size={16} className="text-primary"/>
            Order Status Breakdown
          </h2>
          {orderStatusData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-500 text-sm">No order data</div>
          ) : (
            <div className="flex gap-4 items-center">
              <ResponsiveContainer width="55%" height={160}>
                <PieChart>
                  <Pie data={orderStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={68} paddingAngle={3}>
                    {orderStatusData.map((d, i) => <Cell key={i} fill={d.color}/>)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v + ' orders', n]}/>
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2 py-1">
                {orderStatusData.map(d => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }}/>
                    <span className="text-xs text-gray-300 flex-1">{d.name}</span>
                    <span className="text-xs font-semibold text-white">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Category product count breakdown */}
        <div className="bg-surface-card border border-surface-border rounded-xl p-5">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Archive size={16} className="text-gold"/>
            Products by Category
          </h2>
          {cats.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-500 text-sm">No category data</div>
          ) : (
            <div className="flex gap-4">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart>
                  <Pie data={cats} dataKey="product_count" nameKey="name" cx="50%" cy="50%" outerRadius={70} paddingAngle={3}>
                    {cats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v + ' products', n]}/>
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2 py-2">
                {cats.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }}/>
                    <span className="text-xs text-gray-300 flex-1 truncate">{c.name}</span>
                    <span className="text-xs text-gray-500 font-medium">{c.product_count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent orders — status + date only, NO order totals/amounts */}
      <div className="bg-surface-card border border-surface-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-white flex items-center gap-2">
            <ShoppingCart size={16} className="text-primary"/>
            Recent Orders
          </h2>
          <Link to="/orders" className="text-primary text-xs flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowRight size={12}/>
          </Link>
        </div>
        <div className="space-y-1">
          {recentOrders.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No orders yet</p>
          ) : recentOrders.map(order => (
            <Link
              key={order.id}
              to="/orders"
              className="flex items-center justify-between py-2.5 px-2 rounded-lg hover:bg-surface-elevated transition-colors border-b border-surface-border/40 last:border-0"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-surface-elevated rounded-lg flex items-center justify-center flex-shrink-0">
                  {STATUS_ICONS[order.status] || <Package size={13} className="text-gray-400"/>}
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Order #{order.id}</p>
                  <p className="text-xs text-gray-500">{order.created_at ? new Date(order.created_at).toLocaleDateString('en-ZA') : '—'}</p>
                </div>
              </div>
              <StatusBadge status={order.status}/>
            </Link>
          ))}
        </div>
      </div>

      {/* Top products — by unit count only, no revenue amounts */}
      {topProdsData.length > 0 && (
        <div className="bg-surface-card border border-surface-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <Package size={16} className="text-primary"/>
              Top Products by Units Ordered
            </h2>
            <Link to="/products" className="text-primary text-xs flex items-center gap-1 hover:gap-2 transition-all">
              All products <ArrowRight size={12}/>
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={topProdsData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" horizontal={false}/>
              <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} tickLine={false} axisLine={false} width={130}/>
              <Tooltip formatter={v => [v + ' units', 'Ordered']}/>
              <Bar dataKey="units" fill="#0e947a" radius={[0,4,4,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
